// The one motion system. Every animated thing in the app takes its physics from here, so the app moves with
// one hand. Real springs (Motion), three presets, a small set of durations and a single easing curve.
//
//   snappy: buttons, toggles, chips, counts. Settles fast, no visible overshoot.
//   gentle: panels, sheets, drawers, rows opening and closing, list layout. Soft, unhurried.
//   bouncy: success moments only (a check drawing, a card approved). A small, visible overshoot.
//
// Rules: animate transform and opacity only (layout animations are transforms under the hood), never width,
// height, top or left by hand. With "reduce motion" set in the OS, MotionConfig reducedMotion="user" (App.tsx)
// turns every transform and layout animation off and keeps opacity, so movement becomes a fade.
import type { Transition, Variants } from 'motion/react';

export const spring = {
  snappy: { type: 'spring', stiffness: 520, damping: 38, mass: 0.7 },
  gentle: { type: 'spring', stiffness: 260, damping: 32, mass: 1 },
  bouncy: { type: 'spring', stiffness: 420, damping: 17, mass: 0.8 },
} as const satisfies Record<string, Transition>;

/** Seconds. For the few things that are not springs (fades, colour, the check stroke). */
export const duration = { instant: 0.08, fast: 0.16, base: 0.24, slow: 0.4 } as const;

/** The one easing curve (a soft ease-out), for tweens. Mirrored in CSS as --ease. */
export const ease = [0.22, 1, 0.36, 1] as const;

export const fade: Transition = { duration: duration.fast, ease };

/** A row that appears in a list, and resolves then collapses when it leaves (the list closes the gap). */
export const listItem: Variants = {
  initial: { opacity: 0, y: 8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1, transition: spring.gentle },
  exit: { opacity: 0, scale: 0.96, transition: { ...spring.gentle, opacity: { duration: duration.fast, ease } } },
};

/** Things that enter one after another (starters, sources). */
export const stagger = (step = 0.04, delay = 0): Transition => ({ staggerChildren: step, delayChildren: delay });

/**
 * The side panels (6.3, Kareem 5 Nov: they opened and closed too fast): opening is a calm 0.42s ease-out, closing a
 * quicker 0.32s. The docked panels' width does the same in CSS (assistant.css, .dock); the overlays (the activity panel
 * and the AI panel below 1100px) slide with these. The phone's sheets keep their spring.
 */
export const sidePanel = {
  open: { duration: 0.42, ease } as Transition,
  close: { duration: 0.32, ease: [0.4, 0, 0.2, 1] } as Transition,
};

/** A sheet from the bottom (phone) or a drawer from the right. */
export const sheet = {
  bottom: { initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' } },
  right: { initial: { x: '100%' }, animate: { x: 0 }, exit: { x: '100%' } },
} as const;

/** The soft scrim behind sheets and the command palette. */
export const scrim: Variants = {
  initial: { opacity: 0 }, animate: { opacity: 1, transition: { duration: duration.base, ease } }, exit: { opacity: 0, transition: { duration: duration.fast, ease } },
};

/** True when the person asked the OS for less motion (for things Motion does not handle, like the canvas waveform). */
export const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
